/**
 * Formats a Date object to ISO 8601 DateTime string without timezone conversion.
 * This preserves the local date/time values exactly as they are in the Date object.
 *
 * @param {Date | string} date - The date to format (Date object or ISO string)
 * @returns {string} ISO 8601 DateTime string (e.g., "2025-12-05T14:30:00.000000")
 *
 * @example
 * const date = new Date(2025, 11, 5, 14, 30, 0);
 * formatDateToISOWithoutTimezone(date);
 * // Returns: "2025-12-05T14:30:00.000000"
 */
export const formatDateToISOWithoutTimezone = (date: Date | string) => {
  // If it's already a string, return as-is
  if (typeof date === "string") {
    return date;
  }

  // If it's not a Date object, return as-is
  if (!(date instanceof Date)) {
    return date;
  }

  // Extract date components without timezone conversion
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  const milliseconds = String(date.getMilliseconds()).padStart(3, "0");

  // Format as ISO DateTime with microseconds (6 digits)
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}000`;
};
